import { useMemo, useState } from 'react';
import { RecordEditor } from '@/entities/shared/record/record-editor';
import { useTasks } from '@/entities/task/hooks/use-tasks';
import type { Task } from '@/entities/task/schemas/task';
import { taskKindSchema } from '@/entities/task/schemas/task-kind';
import { useEditorParam } from '@/shared/components/editor-sheet/use-editor-param';
import { ListCard } from '@/shared/components/list/list-card';
import { ListSkeleton } from '@/shared/components/list/list-skeleton';
import { Page } from '@/shared/components/page/page';
import { PageHeader } from '@/shared/components/page/page-header';
import { TabCount } from '@/shared/components/tab-count';
import { useTranslation } from '@/shared/i18n/use-translation';
import { Tabs } from '@/shared/ui/tabs/tabs';
import { TabsList } from '@/shared/ui/tabs/tabs-list';
import { TabsTrigger } from '@/shared/ui/tabs/tabs-trigger';
import { tasksForTab } from '../helpers/tasks-for-tab';
import type { TasksTab } from '../schemas/tasks-tab';
import { TaskEditor } from './task-editor';
import { TaskRow } from './task-row';
import { TasksEmpty } from './tasks-empty';

export function TasksScreen() {
  const { t } = useTranslation();
  const tasks = useTasks();
  const editor = useEditorParam();
  const [tab, setTab] = useState<TasksTab>('open');
  const shown = useMemo(() => tasksForTab(tasks ?? [], tab), [tasks, tab]);
  const counts = useMemo(
    () => ({
      open: tasksForTab(tasks ?? [], 'open').length,
      recommendation: tasksForTab(tasks ?? [], 'recommendation').length,
    }),
    [tasks],
  );
  const kind = taskKindSchema.safeParse(editor.params.get('kind'));

  function add() {
    editor.open('new', tab === 'recommendation' ? { kind: 'recommendation' } : {});
  }

  return (
    <Page>
      <PageHeader
        title={t('Tasks')}
        description={t('Pending actions, advice to follow and periodic checks of the kit.')}
        primaryAction={{ label: t('Add'), onClick: add }}
      />
      {/* The tab values are the TasksTab literals below. */}
      <Tabs value={tab} onValueChange={(value) => setTab(value as TasksTab)}>
        <TabsList>
          <TabsTrigger value="open">
            {t('Open tasks')} <TabCount count={counts.open} />
          </TabsTrigger>
          <TabsTrigger value="recommendation">
            {t('Recommendations')} <TabCount count={counts.recommendation} />
          </TabsTrigger>
          <TabsTrigger value="closed">{t('Closed')}</TabsTrigger>
        </TabsList>
      </Tabs>
      {!tasks ? (
        <ListSkeleton />
      ) : shown.length === 0 ? (
        <TasksEmpty tab={tab} onAdd={add} />
      ) : (
        <ListCard>
          {shown.map((view) => (
            <TaskRow key={view.id} view={view} onOpen={() => editor.open(view.id)} />
          ))}
        </ListCard>
      )}
      <RecordEditor<Task> collection="tasks" id={editor.editing}>
        {(view) => (
          <TaskEditor view={view} initialKind={kind.success ? kind.data : undefined} onClose={editor.close} />
        )}
      </RecordEditor>
    </Page>
  );
}
